import React, { useEffect, useState, useContext } from 'react';

import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { useIsDesktop } from '../../../../../hooks';
import { WysiwygConfigMode } from '../../../WysiwygInput';
import { ApiUrlContext } from '../../../context';
import { useHeight } from '../hooks';
import { useAdditionalFetchDataHelpersPluginCallback } from '../../AdditionalFetchDataHelpersPlugin';

import { ImageComponent } from './ImageComponent';

const IMAGE_HEIGHT_SIZE_L = '252';
const IMAGE_WIDTH_SIZE_L = '448';
const IMAGE_HEIGHT_SIZE_S = '130';
const IMAGE_WIDTH_SIZE_S = '232';

const getCurrentCommentsImageSize = (isDesktop: boolean) => {
  if (isDesktop) {
    return {
      height: `${IMAGE_HEIGHT_SIZE_L}px`,
      width: `${IMAGE_WIDTH_SIZE_L}px`,
    };
  }

  return {
    height: `${IMAGE_HEIGHT_SIZE_S}px`,
    width: `${IMAGE_WIDTH_SIZE_S}px`,
  };
};

interface ImageProps {
  alt: string;
  id: string;
  description?: string;
  mode?: WysiwygConfigMode;
  editable?: boolean;
}

export const Image = ({ alt, description, id, mode, editable }: ImageProps) => {
  const { editorHeight, maxHeight, minHeight } = useHeight();
  const isDesktop = useIsDesktop();
  const contextHelpers = useAdditionalFetchDataHelpersPluginCallback();
  const isComments = mode === 'comments';
  const host = useContext(ApiUrlContext);

  const [src, setSrc] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (src) return;

    if (contextHelpers?.getAttachmentUrl) {
      const fetchAttachmentUrl = async () => {
        if (contextHelpers.getAttachmentUrl) {
          await contextHelpers
            .getAttachmentUrl(id)
            .then((res) => {
              setSrc(() => res);
            })
            .catch((err) => {
              console.error(err);

              setHasError(() => true);
            });
        }
      };

      fetchAttachmentUrl();
    }
  }, [src, id, contextHelpers, host]);

  const { height: commentsHeight, width: commentsWidth } =
    getCurrentCommentsImageSize(isDesktop);

  const calculateMaxHeight = () => {
    if (isComments) return commentsHeight;
    if (editable) return editorHeight;
    return maxHeight;
  };

  const calculateMinHeight = () => {
    if (isComments) return commentsHeight;

    return minHeight;
  };

  return (
    <Box display="flex" flexDirection="column" gap="12" id={id}>
      <ImageComponent
        alt={alt}
        src={src}
        maxHeight={calculateMaxHeight()}
        minHeight={calculateMinHeight()}
        width={isComments ? commentsWidth : '100%'}
        hasError={hasError}
      />

      {description && (
        <Typography variant="caption" color="text.light.tertirary">
          {description}
        </Typography>
      )}
    </Box>
  );
};
